import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { sortListBy } from '@celerofinancas/core-ts';
import { UserState } from '@celerofinancas/state-management';
import { ModalRef, ModalService } from '@celerofinancas/ui-modals';
import { environment } from '@environments/environment';
import { Store } from '@ngxs/store';
import { InitialJourneyModalComponent } from '@pages/initial-journey-modal/initial-journey-modal.component';
import { PolicyAcceptanceService } from '@shared/services/policy-acceptance/policy-acceptance.service';
import {
  STEP_BY_STEP,
  STEP_INITIAL_JOURNEY,
} from '@pages/initial-journey-modal/step-by-step-initial-journey.const';
import { filter, finalize, firstValueFrom, map, of, switchMap, take } from 'rxjs';

interface AcceptanceTermInfo {
  url: string;
  release_date: string;
}

interface AcceptanceTerm extends AcceptanceTermInfo {
  version: number;
}

interface AcceptanceTermValue {
  body: string;
  release_date: string;
  version: string;
}

/**
 * Service to check if the user is accessing the application for the first time.
 */
@Injectable({
  providedIn: 'root',
})
export class CheckFirstAccessService {
  /**
   * Store Service
   */
  private readonly store = inject(Store);

  /**
   * Service for handling modal operations.
   */
  private readonly cdsModal = inject(ModalService);

  /**
   * HttpClient for loading markdown files
   */
  private readonly http = inject(HttpClient);

  /**
   * Terms of use acceptance, shared with everything else that depends on it
   */
  private readonly policyAcceptance = inject(PolicyAcceptanceService);

  /**
   *  Signal to indicate if the acceptance term is loading
   */
  public isLoadingTerm = signal(false);

  /**
   * Observable that emits whether the user has accepted the privacy policy.
   *
   * Delegates to `PolicyAcceptanceService` so the modal and every other
   * feature gated by the acceptance — the CSAT surveys, for one — answer to
   * the same predicate.
   */
  public policyAcceptanceDate$ = this.policyAcceptance.hasAccepted$;

  /**
   * Reference to the current initial journey modal, when open.
   */
  private modalRef: ModalRef | null = null;

  /**
   * Opens the initial journey modal.
   */
  public openModalInitialJourney(): void {
    if (this.modalRef) {
      return;
    }

    this.modalRef = this.cdsModal.open(
      InitialJourneyModalComponent,
      STEP_BY_STEP[STEP_INITIAL_JOURNEY.termOfAcceptance],
      { modal: { closeStrategy: 'static' } },
    );

    this.modalRef.afterClosed$
      .pipe(
        take(1),
        switchMap(() => this.policyAcceptanceDate$.pipe(take(1))),
      )
      .subscribe((accepted: boolean) => {
        this.modalRef = null;
        if (!accepted) {
          this.openModalInitialJourney();
        }
      });
  }

  /**
   * Verifies if the user has accepted the terms or completed the initial journey.
   */
  public verifyAcceptTermsOrComplete(): void {
    this.store
      .select(UserState.getState)
      .pipe(filter((user) => !!user.pk))
      .subscribe(() => {
        this.policyAcceptanceDate$.pipe(take(1)).subscribe((accepted: boolean) => {
          if (!accepted) {
            this.openModalInitialJourney();
          }
        });
      });
  }

  /**
   * Loads the latest active acceptance term and its content.
   * @returns Promise resolving to an object containing the acceptance term details and content.
   */
  public getAcceptanceTerm(): Promise<{ value: AcceptanceTermValue } | null> {
    const acceptanceTerms = 'privacy/acceptance-terms.json';
    const { staticUrl } = environment;
    this.isLoadingTerm.set(true);

    return firstValueFrom(
      this.http.get<Record<string, AcceptanceTermInfo>>(`${staticUrl}/${acceptanceTerms}`).pipe(
        finalize(() => this.isLoadingTerm.set(false)),
        map((acceptanceTermsObject: Record<string, AcceptanceTermInfo>) => {
          const acceptanceArray = this.filterActiveAcceptanceTerms(
            this.acceptanceTermsObjectToArray(acceptanceTermsObject),
          );
          return sortListBy(acceptanceArray, 'version').pop();
        }),
        switchMap((acceptanceTerm) => {
          if (!acceptanceTerm) {
            return of(null);
          }

          const { version, url, ...data } = acceptanceTerm;

          return this.http
            .get(`${staticUrl}/${acceptanceTerms.split('/')[0]}/${url.substring(2)}`, {
              responseType: 'text',
            })
            .pipe(
              map((text: string) => ({
                value: {
                  ...data,
                  version: `${version}`,
                  body: this.convertMarkDown(text),
                },
              })),
            );
        }),
      ),
    );
  }

  /**
   * Converts a markdown string to HTML.
   * @param markdown Text in markdown
   * @returns
   */
  public convertMarkDown(markdown: string): string {
    let html = markdown;

    html = html.replace(/^## (.*)$/gm, '<h4>$1</h4>');
    html = html.replace(/^# (.*)$/gm, '<h3>$1</h3>');
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    html = html.replace(/_(.*?)_/g, '<em>$1</em>');
    html = html.replace(/^\s*[IVXLCDM]+\.\s+(.*)$/gm, '<li class="roman-item">$1</li>');
    html = html.replace(
      /(^<li class="roman-item">.*<\/li>$\n?)+/gm,
      (match: string) => `<ol type="I">${match}</ol>`,
    );
    html = html.replace(/^- (.*)$/gm, '<li class="bullet-item">$1</li>');
    html = html.replace(
      /(<li class="bullet-item">.*<\/li>\n*)+/gm,
      (match: string) => `<ul>${match}</ul>`,
    );
    html = html.replace(/ class="roman-item"| class="bullet-item"/g, '');
    html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2">$1</a>');
    html = html.replace(/\n\s*\n/g, '<br/>');

    return html;
  }

  /**
   *
   * @param acceptanceTerms Acceptance terms object
   * @returns
   */
  private acceptanceTermsObjectToArray(
    acceptanceTerms: Record<string, AcceptanceTermInfo>,
  ): (AcceptanceTermInfo & { version: number })[] {
    return Object.entries(acceptanceTerms).map(([version, acceptanceInfo]) => ({
      version: +version,
      ...acceptanceInfo,
    }));
  }

  /**
   * Filters acceptance terms array by release_date
   *
   * @param acceptanceTerms Acceptance terms array
   * @returns Acceptance terms with release date before today
   */
  private filterActiveAcceptanceTerms(acceptanceTerms: AcceptanceTerm[]) {
    return acceptanceTerms.filter(({ release_date }) => new Date(release_date) < new Date());
  }
}
